import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import GuestBenefits from '../components/checkout/GuestBenefits';
import { formatCurrency } from '../utils/formatting';

// Reusable component
const FormInput = ({ id, placeholder, type = 'text', value, onChange, required }) => (
  <input
    type={type}
    id={id}
    name={id}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    required={required}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
  />
);

export default function CheckoutPage() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const brandColor = '#e46809';

  const [email, setEmail] = useState(user?.email || '');
  const [fullName, setFullName] = useState(
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''
  );
  const [isProcessing, setIsProcessing] = useState(false);

  // Called after Paystack payment succeeds
  const onPaystackSuccess = async (reference) => {
    setIsProcessing(true);
    console.log('%c1. Paystack Success Triggered!', 'color: green; font-weight: bold;', reference);

    const transactionId = reference.reference;
    if (!transactionId) {
      alert('CRITICAL ERROR: Paystack did not return a reference ID.');
      setIsProcessing(false);
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/donations/fund`;
      const payload = {
        transactionId: transactionId,
        donorEmail: email,
        donorFullName: fullName,
        cartTotal: cartTotal, // Add this line
        cartItems: cartItems.map((item) => ({
          campaignId: item.id,
          amount: item.product_price,
        })),
      };

      console.log(`%c2. Sending FETCH POST to: ${url}`, 'color: blue;', { payload });
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      console.log('%c3. Received response from backend.', 'color: blue;');
      console.log(`   - Status: ${response.status} (${response.statusText})`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`%c   - ERROR Body from backend:`, 'color: red; font-weight: bold;', errorText);
        let errorMessage = 'An unknown error occurred on our server.';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = 'Our server returned an unexpected response. Please contact support.';
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('   - Success Body from backend:', responseData);

      console.log(
        '%c4. Backend process successful. Clearing cart and navigating...',
        'color: green; font-weight: bold;'
      );
      clearCart();
      navigate('/checkout/success');
    } catch (error) {
      console.error('%c5. CRITICAL ERROR caught in onPaystackSuccess:', 'color: red; font-weight: bold;', error);
      alert(`CRITICAL ERROR: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const handlePaystackPayment = () => {
    if (!email || !fullName) {
      alert('Please fill in your name and email to continue.');
      return;
    }
  // Check if the PaystackPop object is available on the window object
  if (!window.PaystackPop) {
    // If it's not available, it means the script hasn't loaded yet.
    // Alert the user and stop the function.
    alert('Paystack payment gateway is not available at the moment. Please check your internet connection or try again later.');
    console.error('PaystackPop script has not loaded.');
    return;
  }
    const handler = window.PaystackPop.setup({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: email,
      amount: cartTotal * 100, // in kobo
      ref: new Date().getTime().toString(),
      onClose: () => {
        console.log('Payment closed/cancelled by user');
      },
      callback: (response) => {
        console.log('Paystack callback:', response);
        onPaystackSuccess(response);
      },
    });

    handler.openIframe();
  };

  useEffect(() => {
    if (cartItems.length === 0 && location.pathname.includes('/checkout') && !isProcessing) {
      navigate('/campaigns');
    }
  }, [cartItems, navigate, location.pathname, isProcessing]);

  if (cartItems.length === 0 && !isProcessing) {
    return <div className="p-8 text-center">Your cart is empty. Redirecting...</div>;
  }

  if (isProcessing) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Processing Your Donation...</h1>
        <p className="mt-2 text-lg text-gray-500 dark:text-gray-400">
          Please do not refresh or close this page.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6">Checkout</h1>
          <div className="space-y-6">
            {user ? (
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">You are signed in as:</h2>
                <p className="text-lg font-medium text-gray-700 dark:text-gray-200 mt-2">{fullName}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{email}</p>
              </div>
            ) : (
              <>
                <GuestBenefits />
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">Guest Checkout</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Have an account?{' '}
                    <Link to="/login" className="font-medium" style={{ color: brandColor }}>
                      Sign In
                    </Link>
                  </p>
                </div>
                <FormInput
                  id="fullName"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
                <FormInput
                  id="email"
                  placeholder="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </>
            )}

            <button
              type="button"
              onClick={handlePaystackPayment}
              className="w-full py-4 bg-orange-600 text-white font-semibold text-lg rounded-lg shadow-lg transition-transform transform hover:scale-105"
            >
              Pay with Paystack
            </button>
            <p className="text-center text-sm text-gray-500">Secure payment powered by Paystack.</p>
          </div>
        </div>

        <div>
          <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-600 pb-4 mb-4">
              Order Summary
            </h2>
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-4">
                  <img
                    src={item.product_image_url}
                    alt={item.product_name}
                    className="w-16 h-16 rounded-md bg-white dark:bg-slate-700 p-1 object-cover"
                  />
                  <div className="flex-grow">
                    <p className="font-semibold text-gray-800 dark:text-white">{item.product_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      For: {item.first_name} {item.last_name}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-800 dark:text-white">
                    {formatCurrency(item.product_price)}
                  </p>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-300 dark:border-slate-600 mt-6 pt-4 space-y-2">
              <div className="flex justify-between font-bold text-lg text-gray-900 dark:text-white">
                <span>Total</span>
                <span>{formatCurrency(cartTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
